import { useCallback, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useGlobal } from 'context/global/GlobalContext';
import validateNewDtcVisitor from 'utils/validateNewDtcVisitor';
import {
  appendTrackingParamsToUrl,
  ExperienceData,
  getBaseUrl,
} from 'utils/linkTracking';

const useRedirectLink = () => {
  const { logEvent, productDetails } = useGlobal();
  const history = useHistory();

  // Experience data for link tracking (matches ProductDetails.tsx behavior)
  const experienceData: ExperienceData | null = useMemo(() => {
    if (!productDetails?.product) return null;
    return {
      id:
        productDetails.product?.experienceDetails?.experienceBrijID ||
        productDetails.product?.id ||
        '',
      name: productDetails.product?.name || '',
      title:
        productDetails.product?.displayName ||
        productDetails.product?.name ||
        '',
    };
  }, [productDetails?.product]);

  const redirectToExternalLink = useCallback(
    (
      url?: string | URL,
      target?: string,
      features?: string,
      internal?: boolean
    ) => {
      const urlString =
        typeof url === 'string' ? url : url ? url.toString() : '';

      if (!urlString) {
        return;
      }

      if (internal) {
        history.push(urlString);
        return;
      }

      const urlWithProtocol =
        urlString.includes('https://') || urlString.includes('http://')
          ? urlString
          : `https://${urlString}`;

      const linkTrackingParams = productDetails?.product?.linkTrackingParams;
      const trackedUrl =
        linkTrackingParams?.enabled && experienceData
          ? appendTrackingParamsToUrl(
              urlWithProtocol,
              linkTrackingParams,
              experienceData
            )
          : urlWithProtocol;

      logEvent({
        eventType: 'ENGAGEMENTS',
        event: 'WEBSITE_VISITS',
        data: { url: getBaseUrl(trackedUrl) },
        customerNewDtcVisitor: validateNewDtcVisitor(
          productDetails?.brand?.website,
          productDetails?.brand?.shopifyStoreName
        ),
      });

      window.open(trackedUrl, target, features);
    },
    [
      experienceData,
      history,
      logEvent,
      productDetails?.brand?.shopifyStoreName,
      productDetails?.brand?.website,
      productDetails?.product?.linkTrackingParams,
    ]
  );

  return redirectToExternalLink;
};

export default useRedirectLink;
